/**
 * 生成单个 Section
 * 支持逐区块生成，提高生成质量
 */

import { BlockManager } from "../../src/utils/BlockManager";
import { StandardType } from "../../src/constants/blockType";
import type { TextNode } from "../../src/typings";

export interface SectionPayload {
  type: "standard-section";
  data: Record<string, any>;
  attributes?: Record<string, any>;
  children: ColumnPayload[];
}

export interface WrapperPayload {
  type: "standard-wrapper";
  data: Record<string, any>;
  attributes?: Record<string, any>;
  children: SectionPayload[];
}

export interface ColumnPayload {
  type: "standard-column";
  data: Record<string, any>;
  attributes?: Record<string, any>;
  children: ElementPayload[];
}

export interface ElementPayload {
  type: string;
  data: Record<string, any>;
  attributes?: Record<string, any>;
  children?: Array<ElementPayload | TextNode>;
}

export interface GenerateSectionInput {
  section: SectionPayload | WrapperPayload;
}

export interface GenerateSectionOutput {
  success: boolean;
  section?: any;
  error?: string;
}

/**
 * 创建元素
 */
function createElement(payload: ElementPayload): any {
  const blockDef = BlockManager.getBlockByType(payload.type as any);

  if (!blockDef?.create) {
    throw new Error(`Block type not found: ${payload.type}`);
  }

  // 处理嵌套 children
  let children = payload.children;

  // 处理 hero 的 children
  if (payload.type === StandardType.STANDARD_HERO && Array.isArray(children)) {
    children = children.map((child: any) => {
      if (child.type && child.type.startsWith("standard-")) {
        return createElement(child as ElementPayload);
      }
      return child;
    });
  }

  // 处理 navbar 的 children
  if (
    payload.type === StandardType.STANDARD_NAVBAR &&
    Array.isArray(children)
  ) {
    children = children.map((child: any) => {
      const linkBlockDef = BlockManager.getBlockByType(
        StandardType.STANDARD_NAVBAR_LINK as any
      );
      if (linkBlockDef?.create) {
        return linkBlockDef.create({
          attributes: child.attributes,
          data: child.data || {},
          children: child.children || [],
        });
      }
      return child;
    });
  }

  // 处理 social 的 children
  if (
    payload.type === StandardType.STANDARD_SOCIAL &&
    Array.isArray(children)
  ) {
    children = children.map((child: any) => {
      const itemBlockDef = BlockManager.getBlockByType(
        StandardType.STANDARD_SOCIAL_ELEMENT as any
      );
      if (itemBlockDef?.create) {
        return itemBlockDef.create({
          attributes: child.attributes,
          data: child.data || {},
          children: child.children || [],
        });
      }
      return child;
    });
  }

  return blockDef.create({
    attributes: payload.attributes,
    data: payload.data,
    children: children || [],
  });
}

/**
 * 创建 Column
 */
function createColumn(payload: ColumnPayload): any {
  const blockDef = BlockManager.getBlockByType(
    StandardType.STANDARD_COLUMN as any
  );
  if (!blockDef?.create) {
    throw new Error(`Block type not found: ${StandardType.STANDARD_COLUMN}`);
  }

  const children = payload.children.map(createElement);

  return blockDef.create({
    attributes: payload.attributes,
    data: payload.data,
    children,
  });
}

/**
 * 创建 Section
 */
function createSection(payload: SectionPayload): any {
  const blockDef = BlockManager.getBlockByType(
    StandardType.STANDARD_SECTION as any
  );
  if (!blockDef?.create) {
    throw new Error(`Block type not found: ${StandardType.STANDARD_SECTION}`);
  }

  const children = payload.children.map(createColumn);

  return blockDef.create({
    attributes: payload.attributes,
    data: payload.data,
    children,
  });
}

/**
 * 创建 Wrapper
 */
function createWrapper(payload: WrapperPayload): any {
  const blockDef = BlockManager.getBlockByType(
    StandardType.STANDARD_WRAPPER as any
  );
  if (!blockDef?.create) {
    throw new Error(`Block type not found: ${StandardType.STANDARD_WRAPPER}`);
  }

  // Wrapper 的 children 是 Section 数组
  const children = payload.children.map(createSection);

  return blockDef.create({
    attributes: payload.attributes,
    data: payload.data,
    children,
  });
}

/**
 * 生成单个 Section 或 Wrapper
 */
export function generateSection(
  input: GenerateSectionInput
): GenerateSectionOutput {
  try {
    const { section } = input;

    if (!section || !section.type) {
      throw new Error("Invalid section: must have type field");
    }

    if (section.type === "standard-wrapper") {
      const wrapperPayload = section as WrapperPayload;
      if (
        !Array.isArray(wrapperPayload.children) ||
        wrapperPayload.children.length === 0
      ) {
        throw new Error("Wrapper must have at least one section");
      }
      const generatedWrapper = createWrapper(wrapperPayload);
      return {
        success: true,
        section: generatedWrapper,
      };
    } else if (section.type === "standard-section") {
      const sectionPayload = section as SectionPayload;
      if (
        !Array.isArray(sectionPayload.children) ||
        sectionPayload.children.length === 0
      ) {
        throw new Error("Section must have at least one column");
      }
      const generatedSection = createSection(sectionPayload);
      return {
        success: true,
        section: generatedSection,
      };
    } else {
      throw new Error(
        `Invalid section type: ${section.type}. Must be 'standard-section' or 'standard-wrapper'`
      );
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export const GENERATE_SECTION_TOOL = {
  name: "generate_section",
  description: `生成单个 Section（区块）或 Wrapper（容器）。适合逐区块生成邮件模板。

⚠️ 请先调用 get_guidelines 了解关键规则！

支持的类型:
- \`standard-section\`: 独立的内容块（children 是 Column 数组）
- \`standard-wrapper\`: 容器，包裹多个相关的 Section（children 是 Section 数组）

何时使用 Wrapper:
- 当多个 Section 在视觉上属于同一个容器/分组时
- 例如：两个内容块（顶部和底部）属于同一个视觉分组

注意事项:
- Section/Wrapper 的 padding 必须显式写出（padding-top/bottom/left/right）
- Image 的 width 只支持 px 或留空，不支持百分比
- 每个元素必须有 type 和 data 字段

输入:
- section: Section 或 Wrapper 结构（必须包含 type, data, attributes, children）

示例 1: Section
{
  "section": {
    "type": "standard-section",
    "data": {},
    "attributes": {
      "background-color": "#FFFFFF",
      "padding-top": "20px",
      "padding-bottom": "20px",
      "padding-left": "0px",
      "padding-right": "0px"
    },
    "children": [{
      "type": "standard-column",
      "data": {},
      "attributes": { "width": "100%" },
      "children": [{
        "type": "standard-paragraph",
        "data": {},
        "attributes": { "align": "center" },
        "children": [{ "text": "Hello" }]
      }]
    }]
  }
}

示例 2: Wrapper（包裹多个 Section）
{
  "section": {
    "type": "standard-wrapper",
    "data": {},
    "attributes": {
      "background-color": "#F5F5F5",
      "padding-top": "30px",
      "padding-bottom": "30px",
      "padding-left": "0px",
      "padding-right": "0px"
    },
    "children": [
      {
        "type": "standard-section",
        "data": {},
        "attributes": { ... },
        "children": [ /* Column 数组 */ ]
      },
      {
        "type": "standard-section",
        "data": {},
        "attributes": { ... },
        "children": [ /* Column 数组 */ ]
      }
    ]
  }
}

输出:
- success: boolean
- section: 生成的 Section 或 Wrapper 元素
- error: 错误信息`,
  inputSchema: {
    type: "object" as const,
    properties: {
      section: {
        type: "object",
        description: "Section 结构",
      },
    },
    required: ["section"],
  },
};
